import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  TextField as MUITextField,
  Box,
  Button,
  DialogTitle,
  Dialog,
  DialogActions,
  Badge,
  Tooltip,
  IconButton,
  DialogContent,
  FormControl,
} from "@material-ui/core";
import { authGet } from "../../../lib";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useDispatch } from "../../../state";
import { useSelector, shallowEqual } from "react-redux";
import { Field, useFormikContext } from "formik";
import { DatePicker } from "formik-material-ui-pickers";
import { TextField, CheckboxWithLabel } from "formik-material-ui";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ShareIcon from "@material-ui/icons/Share";
import SyncIcon from "@material-ui/icons/Sync";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { buttonSubmitted } from "../../../lib/helpers";

function calculatePercentOff(retail, percent) {
  return Math.floor(retail - retail * (percent / 100));
}

// Derive the whole-number "% off" from a retail + a (lower) price. Clamped at 0
// so a partial/inverted value never produces a negative or warped percentage.
function pctOff(retail, price) {
  if (!retail || retail <= 0) return 0;
  const p = Math.round(((retail - price) / retail) * 100);
  return p > 0 ? p : 0;
}

export const Pricing = (props) => {
  const selectStyles = {
    fontSize: '12px',
    height: '24px',
    padding: '2px 12px 2px 5px',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    // borderRadius: '4px',
    backgroundColor: 'transparent',
    width: '100%',
  };
  const siteId = useSelector((state) => state.scopes.site_id);
  const dispatch = useDispatch();
  const { classes } = props;
  const { values, setFieldValue } = useFormikContext();
  const [openShare, setOpenShare] = useState(false);
  const handleCloseShare = () => {
    setOpenShare(false);
  };
  const [openConfirm, setOpenConfirm] = useState(false);
  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };
  const {
    delivery: delivery_list,
    ship_tier: ship_tier_list,
    vendor: vendor_list,
    warranty: warranty_list,
    status: status_list,
  } = useSelector((state) => state.watches.watchLists, shallowEqual);

  const [currentRetail, setCurrentRetail] = useState(
    numberWithCommas(values.price_retail),
  );
  const [runOnce, setRunOnce] = useState(true);
  const [errorAlert, setErrorAlert] = useState(false);
  const [open, setOpen] = useState(false);
  const [percentOffField, setPercentOffField] = useState(Number);
  // Guards the one-time init of the % fields from the loaded prices, so later
  // Retail edits don't re-trigger the init path.
  const [pricingInited, setPricingInited] = useState(false);
  const handleNext = () => {
    props.setActiveStep(2);
  };
  const [percentOffSpecialField, setPercentOffSpecialField] = useState(Number);
  // "Special isn't below Our Price" advisory — a soft note, NOT a red error
  // border. Evaluated on blur only so it never flashes while you're mid-typing.
  const [specialAboveOur, setSpecialAboveOur] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  // Vendor add-on-the-fly state
  const [openVendorDialog, setOpenVendorDialog] = useState(false);
  const [newVendorValue, setNewVendorValue] = useState("");
  const [newVendorFormik, setNewVendorFormik] = useState(null);

  // Warranty add-on-the-fly state
  const [openWarrantyDialog, setOpenWarrantyDialog] = useState(false);
  const [newWarrantyValue, setNewWarrantyValue] = useState("");
  const [newWarrantyFormik, setNewWarrantyFormik] = useState(null);

  useEffect(() => {
    if (newVendorFormik) {
      vendor_list.push(newVendorFormik);
      setFieldValue("vendor_id", newVendorFormik["vendor_id"]);
      setNewVendorFormik(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newVendorFormik]);

  useEffect(() => {
    if (newWarrantyFormik) {
      warranty_list.push(newWarrantyFormik);
      setFieldValue("warranty_id", newWarrantyFormik["warranty_id"]);
      setNewWarrantyFormik(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newWarrantyFormik]);

  // Seed the "% Off" fields from the stored prices ONCE on load. After that the
  // three fields are driven purely by their own handlers:
  //   • edit Retail  → hold % Off, recompute Our Price  (Retail onChange, live)
  //   • edit % Off   → hold Retail, recompute Our Price  (% onChange)
  //   • edit Our     → hold Retail, recompute % Off      (handlePriceChange)
  // Retail recomputes live as you type (matching the grid); it holds % Off and
  // never derives it, so a partial value can flash Our Price but can't warp the %.
  // handleRetailBlur repeats the same recompute as a final commit safety.
  useEffect(() => {
    if (pricingInited) return;
    const retail = stringToNumber(values.price_retail);
    if (retail <= 0) return; // wait until real prices have loaded
    setPercentOffField(pctOff(retail, stringToNumber(values.price_our)));
    if (values.price_special) {
      setPercentOffSpecialField(pctOff(retail, stringToNumber(values.price_special)));
    }
    setPricingInited(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.price_retail, pricingInited]);
  useEffect(() => {
    if (!values.price_our) {
      setFieldValue("price_our", 0);
      setPercentOffField(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.price_our]);
  useEffect(() => {
    if (percentOffField === 100) {
      setPercentOffField(0);
    }
    if (percentOffSpecialField === 100) {
      setPercentOffSpecialField(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percentOffField, percentOffSpecialField]);

useEffect(() => {
  // Date Added / date_recent is the "when added" date (= act_date_added on new
  // items) and is shown in the grid independently of the Recent flag. So do NOT
  // clear it when show_recent is off — that used to blank the field on load for
  // any non-recent item (and could wipe the stored value on save). Turning
  // Recent ON still defaults an empty date to today as a convenience.
  if (values.show_recent && !values.date_added) {
    setFieldValue("date_added", new Date().toISOString());
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [values.show_recent]);
  useEffect(() => {
    if (values.vendor_id) {
      let today = new Date();
      setFieldValue("vendor_exp", today.toISOString());
    } else setFieldValue("vendor_exp", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.vendor_id]);
  useEffect(() => {
    if (values.show_reduced) {
      let today = new Date();
      setFieldValue("date_reduced", today.toISOString());
    } else setFieldValue("date_reduced", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.show_reduced]);
  useEffect(() => {
    if (!values.price_on_special) {
      setFieldValue("price_special", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.price_on_special]);

  useEffect(() => {
    props.setRetired(values.retired || false );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.retired]);
  useEffect(() => {
    if (!values.price_special) {
      setFieldValue("price_special", 0);
      setPercentOffSpecialField(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.price_special]);

  // Retail commits on blur: hold the current % Off, recompute Our Price (and the
  // Special price, when the item is on special). Uses the final field value so
  // partial/mid-typing values never reach the calculation.
  const handleRetailBlur = (event) => {
    const retail = stringToNumber(event.target.value);
    if (retail <= 0) return;
    setFieldValue(
      "price_our",
      numberWithCommas(calculatePercentOff(retail, percentOffField || 0)),
    );
    if (values.price_on_special && (percentOffSpecialField || 0) > 0) {
      setFieldValue(
        "price_special",
        numberWithCommas(calculatePercentOff(retail, percentOffSpecialField)),
      );
    }
  };
  const handlePriceChange = (event) => {
    const po = stringToNumber(event.target.value);
    setPercentOffField(
      parseFloat(
        (
          (Math.floor(stringToNumber(values.price_retail) - po) /
            stringToNumber(values.price_retail)) *
          100
        ).toFixed(0),
      ) || 0,
    );
    setFieldValue("price_our", numberWithCommas(po));
  };
  const handlePriceSpecialChange = (event) => {
    setSpecialAboveOur(false); // hide advisory while typing; re-check on blur
    const po = stringToNumber(event.target.value) || 0;
    setPercentOffSpecialField(
      parseFloat(
        (
          (Math.floor(stringToNumber(values.price_retail) - po) /
            stringToNumber(values.price_retail)) *
          100
        ).toFixed(0),
      ) || 0,
    );
    setFieldValue("price_special", numberWithCommas(po));
  };
  const handlePriceBlur = (event) => {
    const po = stringToNumber(event.target.value);
    if (po === 0) {
      setFieldValue("price_our", 0);
      setPercentOffField(0);
    }
    if (percentOffField >= 50 && percentOffField > 100) {
      setOpen(event.target.id);
    }
  };
  const handlePriceOffBlur = (event) => {
    const priceOff = parseInt(event.target.value);
    if (priceOff === 0) {
      setFieldValue("price_our", numberWithCommas(values.price_retail));
      setPercentOffField(0);
    }
    if (percentOffField >= 50 && percentOffField > 100) {
      setOpen(event.target.id);
    }
  };
  const handlePriceSpecialBlur = (event) => {
    const po = parseInt(event.target.value);
    if (po === 0) {
      setFieldValue("price_special", numberWithCommas(values.price_retail));
      setPercentOffSpecialField(0);
    }
    if (parseInt(percentOffSpecialField) >= 50) {
      setOpen(event.target.id);
    }
    // Shared by both the % Special and Special Price fields. po === 0 sets Special
    // to Retail above, so treat that as the effective Special for the advisory.
    const effectiveSpecial =
      po === 0 ? stringToNumber(values.price_retail) : stringToNumber(values.price_special);
    setSpecialAboveOur(
      effectiveSpecial > 0 &&
        stringToNumber(values.price_our) > 0 &&
        effectiveSpecial >= stringToNumber(values.price_our),
    );
  };
  useEffect(() => {
    if (props.activeStep > 1 || props.requiredPriceBadge) {
      setErrorAlert(true);
      let required = 0;
      if (values.status_id === undefined || values.status_id === null) {
        required = required + 1;
      }
      if (
        values.price_on_special &&
        stringToNumber(values.price_special) >= stringToNumber(values.price_our)
      ) {
        required = required + 1;
      }
      props.setRequiredPriceBadge(required);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.activeStep,
    values.price_on_special,
    values.price_our,
    values.price_special,
    values.status_id,
  ]);
  useEffect(() => {
    props.setStatus(values.status_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.status_id]);
  const checkIfChangingFromPt = (e) => {
    (async () => {
      const ptShare = await authGet([`/site_share/${props.watchId}?site=0`]);
      if (currentRetail === ptShare.data.sharedFields.price_retail) {
        setOpenConfirm(true);
      }
    })();
    setRunOnce(false);
  };
  function numberWithCommas(string) {
    if (string === undefined || string === null || string === "") {
      return "";
    }
    let number = parseFloat(string.toString().replace(/,/g, ""));
    if (isNaN(number)) return ""; // empty / partial input — not "NaN"
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  function stringToNumber(string) {
    if (string === undefined || string === null || string === "") {
      return 0;
    }
    let number = parseFloat(string.toString().replace(/,/g, ""));
    return isNaN(number) ? 0 : number;
  }
  // Select the whole value when a pricing field gains focus, so a click or tab
  // into it lets you type a fresh number without clearing first. Deferred a tick
  // so the browser's click-to-place-caret doesn't collapse the selection.
  const selectPricingField = (e) => {
    const el = e.target;
    setTimeout(() => { try { el.select(); } catch (_) {} }, 0);
  };
  return (
    <Accordion expanded={true} style={{ width: "100%" }}>
      <AccordionSummary
        style={{ cursor: "default" }}
        aria-controls="panel1a-content"
        id="panel1a-header"
        tabIndex={-1}
      >
        <Typography>
          <Badge badgeContent={props.requiredPriceBadge} color="secondary">
            Pricing & Status
          </Badge>
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.grid} style={{ display: "grid" }}>
        <Box display="flex" flexWrap="wrap" flexDirection="column" alignItems="left">

          {/* ── ROW 1: Retail | % | Our Price | Special | % Special | Special Price | Status | Show Recent | Date Added | Show Reduced | Date Reduced ── */}
        <Box display="flex" alignItems="center" mx={1} mb={1} style={{ gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <label style={{ fontSize: 11 }}>Retail:</label>
              <Field
                className={values.price_retail ? classes.gridTwoBlue : classes.gridTwo}
                component={TextField}
                name="price_retail"
                onWheel={(e) => e.target.blur()}
                multiline
                style={{ width: 70 }}
                onChange={(e) => {
                  setFieldValue("price_retail", numberWithCommas(e.target.value));
                  // Live recompute (mirrors the grid): hold % Off, recompute Our
                  // (and Special when on special). Holds the %, never derives it,
                  // so a partial Retail can flash Our Price but can't warp the %.
                  const retail = stringToNumber(e.target.value);
                  if (retail > 0) {
                    setFieldValue(
                      "price_our",
                      numberWithCommas(calculatePercentOff(retail, percentOffField || 0)),
                    );
                    // Only recompute Special when a real % Special exists. With
                    // % Special = 0 this would set Special = Retail (always ≥ Our
                    // → false red flag), so leave it alone in that case.
                    if (values.price_on_special && (percentOffSpecialField || 0) > 0) {
                      setFieldValue(
                        "price_special",
                        numberWithCommas(calculatePercentOff(retail, percentOffSpecialField)),
                      );
                    }
                  }
                  if (runOnce && siteId === 2) {
                    checkIfChangingFromPt(e.target.value);
                  }
                }}
                onBlur={handleRetailBlur}
                onFocus={selectPricingField}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <label style={{ fontSize: 11 }}>%</label>
              <MUITextField
                className={
                  percentOffField >= 50
                    ? classes.gridAbove50
                    : percentOffField
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                onChange={(e) => {
                  setPercentOffField(e.target.value || 0);
                  if (values.price_retail && e.target.value) {
                    const po = calculatePercentOff(stringToNumber(values.price_retail), e.target.value);
                    setFieldValue("price_our", numberWithCommas(po));
                  }
                }}
                onBlur={handlePriceOffBlur}
                onWheel={(e) => e.target.blur()}
                onFocus={selectPricingField}
                id="price_our"
                name="our"
                value={percentOffField}
                style={{ width: 30 }}
                multiline
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <label style={{ fontSize: 11 }}>Our Price:</label>
              <Field
                className={values.price_our ? classes.gridTwoBlue : classes.gridTwo}
                onChange={handlePriceChange}
                onBlur={handlePriceBlur}
                component={TextField}
                name="price_our"
                id="price_our"
                type="number"
                onWheel={(e) => e.target.blur()}
                onFocus={selectPricingField}
                style={{ width: 70 }}
                multiline
              />
            </div>
              <Field
                component={CheckboxWithLabel}
                type="checkbox"
                indeterminate={false}
                name="price_on_special"
                Label={{
                  label: <span style={{ fontSize: 11 }}>Special:</span>,
                  labelPlacement: "top",
                  style: { margin: 0, padding:0, gap: 0 },
                }}
                className={classes.gridTwo}
                style={{ padding: 0 }}
              />
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <label style={{ fontSize: 11 }}>% Special:</label>
              <MUITextField
                className={
                  percentOffSpecialField >= 50
                    ? classes.gridAbove50
                    : percentOffSpecialField
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }
                onChange={(e) => {
                  setSpecialAboveOur(false); // hide advisory while typing; re-check on blur
                  setPercentOffSpecialField(e.target.value || 0);
                  if (values.price_retail && e.target.value) {
                    const po = calculatePercentOff(stringToNumber(values.price_retail), e.target.value);
                    setFieldValue("price_special", numberWithCommas(po));
                  }
                }}
                onBlur={handlePriceSpecialBlur}
                id="price_special"
                onWheel={(e) => e.target.blur()}
                onFocus={selectPricingField}
                value={percentOffSpecialField}
                style={{ width: 30 }}
                multiline
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <label style={{ fontSize: 11 }}>Special Price:</label>
            <Field
              // No red error border for the Special ≥ Our case — that's an
              // advisory, not a field error. Shown as the note below, on blur.
              className={values.price_special ? classes.gridTwoBlue : classes.gridTwo}
              onChange={handlePriceSpecialChange}
              onBlur={handlePriceSpecialBlur}
              component={TextField}
              name="price_special"
              id="price_special"
              type="number"
              onWheel={(e) => e.target.blur()}
              onFocus={selectPricingField}
              style={{ width: 70 }}
              multiline
            />
            {specialAboveOur && (
              <span style={{ fontSize: 9, color: "#d32f2f", lineHeight: 1.1, marginTop: 1, maxWidth: 90 }}>
                Not below Our Price
              </span>
            )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <label style={{ fontSize: 11, ...(errorAlert && (values.status_id === undefined || values.status_id === null) ? { color: '#d32f2f' } : {}) }}>Status:</label>
                <select
                  value={values.status_id !== null && values.status_id !== undefined ? String(values.status_id) : ""}
                  onChange={(e) => setFieldValue("status_id", e.target.value !== "" ? Number(e.target.value) : null)}
                  disabled={!values.item_id}
                  title={!values.item_id ? "New items are created as Pending — save first, then edit the item to change its status." : undefined}
                  className={
                    errorAlert
                      ? values.status_id === undefined || values.status_id === null
                        ? `${classes.gridTwoRed} custom-select`
                        : `${classes.gridTwoBlue} custom-select`
                      : values.status_id !== undefined && values.status_id !== null
                      ? `${classes.gridTwoBlue} custom-select`
                      : `${classes.gridTwo} custom-select`
                  }
                  style={{ ...selectStyles, ...(errorAlert && (values.status_id === undefined || values.status_id === null) ? { border: '1px solid #d32f2f' } : {}) }}
                >
                  {/** <option value="">Select Status</option> */}
                  {(status_list || [])
                    .sort((a, b) => (a.status || "").localeCompare(b.status || ""))
                    .map((s) => (
                      <option key={s.status_id} value={s.status_id}>
                        {s.status}
                      </option>
                    ))}
                </select>
            </div>
            <Box display="flex" alignItems="center" style={{ gap: 16 }}>
              <Field
                component={CheckboxWithLabel}
                type="checkbox"
                indeterminate={false}
                name="show_recent"
                Label={{
                  label: <span style={{ fontSize: 11 }}>Recent</span>,
                  labelPlacement: "top",
                  style: { margin: 0, gap: 0 },
                }}
                className={classes.gridTwo}
                style={{ padding: 0 }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <label style={{ fontSize: 11 }}>Date Recent:</label>            
                <Field
                  className={classes.gridTwoBlue}
                  component={DatePicker}
                  name="date_added"
                  // label="Date Recent"
                  showTodayButton={true}
                  format="PP"
                  style={{ width: 90 }}
                  multiline
                />
              </div>
              <Field
                component={CheckboxWithLabel}
                type="checkbox"
                indeterminate={false}
                name="show_reduced"
                Label={{
                  label: <span style={{ fontSize: 11 }}>Reduced</span>,
                  labelPlacement: "top",
                  style: { margin: 0, gap: 0 },
                }}
                className={classes.gridTwo}
                style={{ padding: 0 }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <label style={{ fontSize: 11 }}>Date Reduced:</label>  
                <Field
                  className={classes.gridTwoBlue}
                  component={DatePicker}
                  name="date_reduced"
                  // label="Date Reduced"
                  showTodayButton={true}
                  format="PP"
                  style={{ width: 90 }}
                  multiline
                />
              </div>
              {/* Display Date: checking it reveals the month/year picker + Hide
                  Month. Drives the "Warranty Dated" row on item.tpl. */}
              <Field
                component={CheckboxWithLabel}
                type="checkbox"
                indeterminate={false}
                name="show_display_date"
                Label={{
                  label: <span style={{ fontSize: 11 }}>Display Date</span>,
                  labelPlacement: "top",
                  style: { margin: 0, gap: 0 },
                }}
                className={classes.gridTwo}
                style={{ padding: 0 }}
              />
              {values.show_display_date ? (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <label style={{ fontSize: 11 }}>Date:</label>
                    <Field
                      className={classes.gridTwoBlue}
                      component={DatePicker}
                      name="display_date"
                      views={["year", "month"]}
                      openTo="month"
                      format="MMM yyyy"
                      style={{ width: 90 }}
                      multiline
                    />
                  </div>
                  <Field
                    component={CheckboxWithLabel}
                    type="checkbox"
                    indeterminate={false}
                    name="display_date_hide_month"
                    Label={{
                      label: <span style={{ fontSize: 11 }}>Hide Month</span>,
                      labelPlacement: "top",
                      style: { margin: 0, gap: 0 },
                    }}
                    className={classes.gridTwo}
                    style={{ padding: 0 }}
                  />
                </>
              ) : null}
            </Box>
          </Box>

          {/* ── ROW 2: Warranty | API | Delivery | Shipping Tier | Select Vendor | Vendor Exp | (Retire + Share TPC) ── */}
          <Box display="flex" alignItems="center" mx={1} mb={1} style={{ gap: 16 }}>
            {siteId === 0 && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <label style={{ fontSize: 11 }}>Warranty:</label>
                  <select
                    value={values.warranty_id || ""}
                    onChange={(e) => {
                      if (e.target.value === "__add_new__") {
                        setFieldValue("warranty_id", null);
                        setNewWarrantyValue("");
                        setOpenWarrantyDialog(true);
                      } else {
                        setFieldValue("warranty_id", e.target.value ? Number(e.target.value) : null);
                      }
                    }}
                    className={
                      values.warranty_id
                        ? `${classes.gridTwoBlue} custom-select`
                        : `${classes.gridTwo} custom-select`
                    }
                    style={selectStyles}
                  >
                    <option value=""></option>
                    {(warranty_list || [])
                      .sort((a, b) => (a.warranty || "").localeCompare(b.warranty || ""))
                      .map((w) => (
                        <option key={w.warranty_id} value={w.warranty_id}>
                          {w.warranty}
                        </option>
                      ))}
                    <option value="__add_new__">+ Add New...</option>
                  </select>
                </div>
                  <Field
                    component={CheckboxWithLabel}
                    type="checkbox"
                    indeterminate={false}
                    name="papers_included"
                    Label={{
                      label: <span style={{ fontSize: 11 }}>API</span>,
                      labelPlacement: "top",
                      style: { margin: 0, padding: 0, gap: 0 },
                    }}
                    className={classes.gridTwo}
                    style={{ padding: 0 }}
                  />
              </>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <label style={{ fontSize: 11 }}>Delivery:</label>
                <select
                  value={values.delivery_id || ""}
                  onChange={(e) => setFieldValue("delivery_id", e.target.value ? Number(e.target.value) : null)}
                  className={
                    values.delivery_id
                      ? `${classes.gridTwoBlue} custom-select`
                      : `${classes.gridTwo} custom-select`
                  }
                  style={selectStyles}
                >
                  <option value="">Select Delivery</option>
                  {(delivery_list || [])
                    .sort((a, b) => (a.delivery || "").localeCompare(b.delivery || ""))
                    .map((d) => (
                      <option key={d.delivery_id} value={d.delivery_id}>
                        {d.delivery}
                      </option>
                    ))}
                </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <label style={{ fontSize: 11 }}>Shipping Tier:</label>
                <select
                  value={values.ship_tier_id || ""}
                  onChange={(e) => setFieldValue("ship_tier_id", e.target.value ? Number(e.target.value) : null)}
                  className={
                    values.ship_tier_id
                      ? `${classes.gridTwoBlue} custom-select`
                      : `${classes.gridTwo} custom-select`
                  }
                  style={selectStyles}
                >
                  <option value="">Shipping Tier</option>
                  {(ship_tier_list || [])
                    .sort((a, b) => (a.ship_tier || "").localeCompare(b.ship_tier || ""))
                    .map((s) => (
                      <option key={s.ship_tier_id} value={s.ship_tier_id}>
                        {s.ship_tier}
                      </option>
                    ))}
                </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <label style={{ fontSize: 11 }}>Select Vendor:</label>  
              <select
                value={values.vendor_id || ""}
                onChange={(e) => {
                  if (e.target.value === "__add_new__") {
                    setFieldValue("vendor_id", null);
                    setNewVendorValue("");
                    setOpenVendorDialog(true);
                  } else {
                    setFieldValue("vendor_id", e.target.value ? Number(e.target.value) : null);
                  }
                }}
                className={
                  values.vendor_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={selectStyles}
              >
                <option value=""></option>
                {(vendor_list || [])
                  .sort((a, b) => (a.vendor || "").localeCompare(b.vendor || ""))
                  .map((v) => (
                    <option key={v.vendor_id} value={v.vendor_id}>
                      {v.vendor}
                    </option>
                  ))}
                <option value="__add_new__">+ Add New...</option>
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <label style={{ fontSize: 11 }}>Vendor Exp:</label>  
              <Field
                className={classes.gridTwoBlue}
                component={DatePicker}
                name="vendor_exp"
                format="PP"
                style={{ width: 120 }}
                multiline
              />
            </div>
            {siteId === 0 && (
              <Box
                display="flex"
                alignItems="center"
                style={{
                  marginLeft: "auto",
                  gap: 12,
                  position: "relative",
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                  borderRadius: 4,
                  padding: "3px 10px",
                }}
              >

                 <span
                  style={{
                    position: "absolute",
                    top: -5,
                    left: 15,
                    padding: "0 4px",
                    background: "#fff",
                    fontSize: 8,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    color: "rgba(0, 0, 0, 0.5)",
                  }}
                >
                  OPTIONS
                </span>
             
                <Field
                  disabled={siteId !== 0}
                  className={classes.gridTwo}
                  component={CheckboxWithLabel}
                  type="checkbox"
                  indeterminate={false}
                  inputProps={{ tabIndex: -1 }}
                  name="retired"
                  Label={{
                    label: <span style={{ fontSize: 11 }}>Retire:</span>,
                    labelPlacement: "top",
                    style: { margin: 0, gap: 0 },
                  }}
                  style={{ padding: 0 }}
                />
                {/* Hide Retail: when checked, storefront item.tpl hides the
                    Retail price line (same as when the price is 0). */}
                <Field
                  className={classes.gridTwo}
                  component={CheckboxWithLabel}
                  type="checkbox"
                  indeterminate={false}
                  inputProps={{ tabIndex: -1 }}
                  name="hide_retail"
                  Label={{
                    label: <span style={{ fontSize: 11 }}>Hide Retail:</span>,
                    labelPlacement: "top",
                    style: { margin: 0, gap: 0 },
                  }}
                  style={{ padding: 0 }}
                />
                <Box position="relative" display="inline-flex" alignItems="center">
                  <Tooltip title={values.tpc_sync_enabled ? "Shared with TPC — click to stop" : "Not shared with TPC — click to share"}>
                    <IconButton
                      aria-label="tpc-sync"
                      tabIndex={-1}
                      size="small"
                      style={{ padding: 2 }}
                      onClick={() =>
                        setFieldValue("tpc_sync_enabled", values.tpc_sync_enabled ? 0 : 1)
                      }
                    >
                      <ShareIcon
                        style={{
                          fontSize: 22,
                          color: values.tpc_sync_enabled ? "#1976d2" : "rgba(0, 0, 0, 0.35)",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                  {values.tpc_sync_enabled ? (
                    <CheckCircleIcon
                      style={{
                        position: "absolute",
                        top: -1,
                        right: -1,
                        fontSize: 13,
                        color: "#2e7d32",
                        background: "#fff",
                        borderRadius: "50%",
                        pointerEvents: "none",
                      }}
                    />
                  ) : null}
                </Box>
              </Box>
            )}
          </Box>

        </Box>
      </AccordionDetails>

      {/* ── Dialogs (unchanged) ── */}
      <Dialog open={open} onClose={handleClose} disableBackdropClick disableEscapeKeyDown>
        <DialogTitle>
          The price entered is less than 50% of the retail price, if this is correct please select allow override otherwise please adjust the pricing.
        </DialogTitle>
        <DialogActions>
          <Button variant="contained" onClick={() => { setFieldValue(open, 0); handleClose(); }} color="secondary">Cancel</Button>
          <Button variant="contained" onClick={handleClose} color="primary">Allow Override</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openConfirm} onClose={handleCloseConfirm} disableBackdropClick disableEscapeKeyDown>
        <DialogTitle>Warning: By changing this field It will be different then Prestige Times Retail Price</DialogTitle>
        <DialogContent><FormControl component="fieldset" /></DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { setFieldValue("price_retail", currentRetail); setRunOnce(true); handleCloseConfirm(); }} color="secondary">Cancel</Button>
          <Button variant="contained" onClick={handleCloseConfirm} color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openShare} onClose={handleCloseShare} disableBackdropClick disableEscapeKeyDown>
        <DialogTitle>Confirm to share retail with TPC</DialogTitle>
        <DialogContent><FormControl component="fieldset" /></DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { props.setTpc_retail(false); handleCloseShare(); }} color="secondary">Don't share</Button>
          <Button variant="contained" onClick={() => { props.setTpc_retail(true); handleCloseShare(); }} color="primary">Share Retail</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openVendorDialog} onClose={() => setOpenVendorDialog(false)} disableBackdropClick disableEscapeKeyDown>
        <DialogTitle>{"Add " + newVendorValue + " To Vendor?"}</DialogTitle>
        <DialogContent>
          <MUITextField autoFocus id="new-vendor-name" value={newVendorValue} onChange={(e) => setNewVendorValue(e.target.value)} label="Name" type="text" style={{ width: "100%" }} />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { setNewVendorValue(""); setOpenVendorDialog(false); }} color="secondary">Cancel</Button>
          <Button variant="contained" onClick={() => { buttonSubmitted({ Add_Edit_Delete: "add", rawAttribute: "vendor", myValue: newVendorValue, attribute: "Vendor", dispatch, siteId: 0, setNewFormik: setNewVendorFormik, myBrandId: null }); setOpenVendorDialog(false); }} color="primary">Yes</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openWarrantyDialog} onClose={() => setOpenWarrantyDialog(false)} disableBackdropClick disableEscapeKeyDown>
        <DialogTitle>{"Add " + newWarrantyValue + " To Warranty?"}</DialogTitle>
        <DialogContent>
          <MUITextField autoFocus id="new-warranty-name" value={newWarrantyValue} onChange={(e) => setNewWarrantyValue(e.target.value)} label="Name" type="text" style={{ width: "100%" }} />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { setNewWarrantyValue(""); setOpenWarrantyDialog(false); }} color="secondary">Cancel</Button>
          <Button variant="contained" onClick={() => { buttonSubmitted({ Add_Edit_Delete: "add", rawAttribute: "warranty", myValue: newWarrantyValue, attribute: "Warranty", dispatch, siteId: 0, setNewFormik: setNewWarrantyFormik, myBrandId: null }); setOpenWarrantyDialog(false); }} color="primary">Yes</Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
};